/** @format */

import React from "react";
import { Box, keyframes } from "@mui/material";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import { flavorForQuestion, WAIT_TIPS } from "./propilotEngagement";

// NOTE: the chat API is request/response — there is no streaming, so the
// stage narration below is simulated on client-side timers. All timing logic
// is contained in this file; if the backend ever ships an SSE endpoint, swap
// the timers for real progress events here and nothing else changes.

const TIP_ROTATE_MS = 4000;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
`;

const shimmerBarSx = {
  borderRadius: "4px",
  background:
    "linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)",
  backgroundSize: "400px 100%",
  animation: `${shimmer} 1.6s ease-in-out infinite`,
};

const formatElapsed = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const LoadingSkeleton = ({ question }) => {
  // Stages flavored to the question's topic (NDR/RTO/SLA/…) so the wait
  // narrates, in plain language, what's happening behind the scenes.
  const stages = React.useMemo(() => flavorForQuestion(question), [question]);
  const [statusIndex, setStatusIndex] = React.useState(0);
  const [elapsed, setElapsed] = React.useState(0);
  // Start each wait at a different tip so repeat users don't always see #1.
  const [tipIndex, setTipIndex] = React.useState(() =>
    Math.floor(Math.random() * WAIT_TIPS.length)
  );

  React.useEffect(() => {
    const timeouts = stages.slice(1).map((stage, i) =>
      setTimeout(() => setStatusIndex(i + 1), stage.delay)
    );
    return () => timeouts.forEach(clearTimeout);
  }, [stages]);

  // Elapsed clock + rotating tips keep ticking past the last stage so a long
  // query (timeout is 45s) never looks frozen.
  React.useEffect(() => {
    const clock = setInterval(() => setElapsed((s) => s + 1), 1000);
    const tips = setInterval(
      () => setTipIndex((i) => (i + 1) % WAIT_TIPS.length),
      TIP_ROTATE_MS
    );
    return () => {
      clearInterval(clock);
      clearInterval(tips);
    };
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        px: { xs: 1.25, md: 2 },
        py: 0.5,
      }}
    >
      <Box
        sx={{
          maxWidth: { xs: "100%", md: "90%" },
          width: 520,
          backgroundColor: "#fff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          p: 2,
          boxShadow: "0 1px 2px 0 rgba(15,23,42,0.04)",
          animation: `${fadeIn} 0.2s ease-in-out`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1.5,
            pb: 1,
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              backgroundColor: "#eef2ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BoltRoundedIcon sx={{ color: "#6366f1", fontSize: 14 }} />
          </Box>
          <Box sx={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>
            ProPilot
          </Box>
          <Box
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: 0,
            }}
          >
            <Box
              key={statusIndex}
              sx={{
                fontSize: 11,
                fontWeight: 500,
                color: "#6366f1",
                animation: `${pulse} 1.5s ease-in-out infinite`,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {stages[statusIndex]?.text}
            </Box>
            <Box
              sx={{
                fontSize: 10.5,
                fontWeight: 600,
                color: "#94a3b8",
                fontVariantNumeric: "tabular-nums",
                flexShrink: 0,
              }}
            >
              {formatElapsed(elapsed)}
            </Box>
          </Box>
        </Box>

        {/* Shape-hint skeleton: a few text lines + a chart-sized block, so the
            placeholder roughly matches the answer that's coming. */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {[80, 95, 60].map((w, i) => (
            <Box
              key={i}
              sx={{
                height: 8,
                width: `${w}%`,
                ...shimmerBarSx,
                animationDelay: `${i * 120}ms`,
              }}
            />
          ))}
          <Box
            sx={{
              height: 64,
              width: "100%",
              mt: 0.5,
              borderRadius: "8px",
              ...shimmerBarSx,
              animationDelay: "360ms",
            }}
          />
        </Box>

        {/* Rotating freight tip — keeps the wait interesting & teaches the product. */}
        <Box
          key={tipIndex}
          sx={{
            mt: 1.5,
            pt: 1,
            borderTop: "1px dashed #e2e8f0",
            fontSize: 11.5,
            color: "#64748b",
            lineHeight: 1.5,
            animation: `${fadeIn} 0.4s ease-in-out`,
          }}
        >
          {WAIT_TIPS[tipIndex]}
        </Box>
      </Box>
    </Box>
  );
};

export default LoadingSkeleton;
